import React, { useEffect, useMemo, useState } from 'react';
import ContextualHelpPanel from './ContextualHelpPanel';
import FormField from './FormField';
import { FIELD_TYPES, createSection, createField, createTemplate, createId } from '../types';
import { isAnswerableField } from '../formUtils';
import ListControls, { EmptyFilterState } from './ListControls';
import {
  ALL_FILTER_VALUE,
  TEMPLATE_SORT_OPTIONS,
  createTemplateControls,
  getOptionLabel,
  getTemplateCategoryOptions,
  getTemplateListView,
} from '../navigationUtils';

const cloneTemplate = (template) => ({
  ...template,
  sections: (template.sections || []).map((section) => ({
    ...section,
    fields: (section.fields || []).map((field) => ({ ...field })),
  })),
});

const createDefaultFieldFormData = () => ({
  type: FIELD_TYPES.TEXT,
  label: '',
  placeholder: '',
  required: false,
  options: '',
  layout: {
    order: '',
    span: '',
    align: 'left',
  },
});

const FIELD_WIDTH_PRESETS = [
  { key: 'auto', label: 'Auto', span: '' },
  { key: 'single', label: 'Full', span: '12' },
  { key: 'half', label: 'Half', span: '6' },
  { key: 'third', label: 'Third', span: '4' },
  { key: 'twoThird', label: 'Two Thirds', span: '8' },
  { key: 'large', label: 'Large', span: '10' },
  { key: 'quarter', label: 'Quarter', span: '3' },
];

const FIELD_ALIGNMENT_OPTIONS = [
  { key: 'left', label: 'Left' },
  { key: 'center', label: 'Center' },
  { key: 'right', label: 'Right' },
];
const toIntOrNull = (value) => {
  const parsed = Number.parseInt(value, 10);
  return Number.isInteger(parsed) ? parsed : null;
};

const getPresetForSpan = (span) => {
  const normalized = String(span || '').trim();

  const preset = FIELD_WIDTH_PRESETS.find((entry) => entry.span === normalized);
  return preset ? preset.key : 'auto';
};

const getSpanFromPreset = (presetKey) => {
  const preset = FIELD_WIDTH_PRESETS.find((entry) => entry.key === presetKey);
  return preset ? preset.span : '';
};

const getFieldLayoutAlign = (field = {}) => {
  const alignValue = String(field.layout?.align || 'left').toLowerCase();
  return alignValue === 'center' || alignValue === 'right' ? alignValue : 'left';
};
const getFieldMetadataName = (fieldName, suffix) => `_df_meta_${suffix}_${fieldName}`;

const sortFieldsByLayoutOrder = (fields = []) =>
  [...fields]
    .map((field, index) => ({
      ...field,
      __index: index,
    }))
    .sort((a, b) => {
      const orderA = toIntOrNull(a.layout?.order);
      const orderB = toIntOrNull(b.layout?.order);

      if (orderA !== null && orderB !== null && orderA !== orderB) {
        return orderA - orderB;
      }

      if (orderA !== null && orderB === null) {
        return -1;
      }

      if (orderA === null && orderB !== null) {
        return 1;
      }

      return a.__index - b.__index;
    })
    .map(({ __index, ...field }) => field);

const normalizeSectionFieldOrder = (fields = []) =>
  sortFieldsByLayoutOrder(fields).map((field, index) => ({
    ...field,
    layout: {
      ...(field.layout || {}),
      order: index + 1,
    },
  }));

const getDisplayFieldSpan = (field = {}) => {
  const configuredSpan = toIntOrNull(field.layout?.span) ?? toIntOrNull(field.span);
  const widthSpan = toIntOrNull(field.layout?.width);

  if (configuredSpan !== null || widthSpan !== null) {
    return Math.min(12, Math.max(1, configuredSpan ?? widthSpan));
  }

  if (field.type === FIELD_TYPES.TEXTAREA || field.type === FIELD_TYPES.TABLE || field.type === FIELD_TYPES.CHECKBOX_GROUP || field.type === FIELD_TYPES.LABEL) {
    return 12;
  }

  return 6;
};

const TemplateBuilder = ({
  templates,
  template,
  onAdd,
  onUpdate,
  onDelete,
  onSelect,
  onClose,
  controls,
  onControlsChange,
  helpConfig,
  openFormRequest = 0,
  formCounts = {},
  isEditing = false,
}) => {
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ name: '', description: '', category: 'interview' });
  const normalizedControls = createTemplateControls(controls);
  const templateView = useMemo(
    () => getTemplateListView(templates || [], normalizedControls),
    [normalizedControls, templates]
  );
  const categoryOptions = useMemo(
    () => getTemplateCategoryOptions(templates || [], normalizedControls.category),
    [normalizedControls.category, templates]
  );

  const activeChips = [];

  if (normalizedControls.search.trim()) {
    activeChips.push({
      id: 'search',
      label: `Search: ${normalizedControls.search.trim()}`,
      onRemove: () => onControlsChange({ search: '' }),
    });
  }

  if (normalizedControls.category !== ALL_FILTER_VALUE) {
    activeChips.push({
      id: 'category',
      label: `Category: ${getOptionLabel(categoryOptions, normalizedControls.category)}`,
      onRemove: () => onControlsChange({ category: ALL_FILTER_VALUE }),
    });
  }

  if (normalizedControls.sortBy !== 'name-asc') {
    activeChips.push({
      id: 'sort',
      label: `Sort: ${getOptionLabel(TEMPLATE_SORT_OPTIONS, normalizedControls.sortBy)}`,
      onRemove: () => onControlsChange({ sortBy: 'name-asc' }),
    });
  }

  useEffect(() => {
    if (isEditing && template) {
      setEditingTemplate(cloneTemplate(template));
    }
  }, [isEditing, template]);

  useEffect(() => {
    if (openFormRequest > 0 && !isEditing) {
      setEditingTemplate(null);
      setFormData({ name: '', description: '', category: 'interview' });
      setShowForm(true);
    }
  }, [isEditing, openFormRequest]);

  const handleCreate = async (event) => {
    event.preventDefault();
    const newTemplate = createTemplate(formData.name);
    newTemplate.description = formData.description;
    newTemplate.category = formData.category;
    const createdTemplate = await onAdd(newTemplate) || newTemplate;
    setFormData({ name: '', description: '', category: 'interview' });
    setShowForm(false);
    setEditingTemplate(cloneTemplate(createdTemplate));
  };

  const handleStartEdit = (selectedTemplate) => {
    setEditingTemplate(cloneTemplate(selectedTemplate));
  };

  const handleSaveTemplate = async (updatedTemplate) => {
    await onUpdate(updatedTemplate.id, updatedTemplate);
    setEditingTemplate(null);
  };

  if (editingTemplate) {
    return (
      <TemplateEditor
        template={editingTemplate}
        onSave={handleSaveTemplate}
        onCancel={() => {
          setEditingTemplate(null);
          if (onClose) {
            onClose();
          }
        }}
      />
    );
  }

  return (
    <div className="card">
      <div className="df-toolbar">
        <div className="df-toolbar-title">
          <h2 className="card-title">Form Templates</h2>
        </div>
        {!showForm && templates && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Create New Template
          </button>
        )}
      </div>

      {helpConfig && <ContextualHelpPanel {...helpConfig} />}

      {showForm && (
        <form
          onSubmit={handleCreate}
          style={{ marginBottom: '20px', padding: '20px', background: '#f8f9fa', borderRadius: '8px' }}
        >
          <h3 style={{ marginBottom: '15px', color: 'var(--primary)' }}>Create New Template</h3>
          <div className="form-group">
            <label className="form-label">Template Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(event) => setFormData({ ...formData, name: event.target.value })}
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(event) => setFormData({ ...formData, description: event.target.value })}
              rows={2}
            />
          </div>
          <div className="form-group">
            <label className="form-label">Category</label>
            <select
              className="form-select"
              value={formData.category}
              onChange={(event) => setFormData({ ...formData, category: event.target.value })}
            >
              <option value="interview">Interview</option>
              <option value="survey">Survey</option>
              <option value="assessment">Assessment</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div className="df-action-group">
            <button type="submit" className="btn btn-primary">Create Template</button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </form>
      )}

      {!templates || templates.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          <p>No templates yet. Create your first template.</p>
        </div>
      ) : (
        <div className="df-list-layout">
          <ListControls
            searchPlaceholder="Search by template name, description, category, section, or field"
            searchValue={normalizedControls.search}
            onSearchChange={(search) => onControlsChange({ search })}
            filters={[
              {
                id: 'template-category-filter',
                label: 'Category',
                value: normalizedControls.category,
                options: categoryOptions,
                onChange: (category) => onControlsChange({ category }),
              },
            ]}
            sortValue={normalizedControls.sortBy}
            sortOptions={TEMPLATE_SORT_OPTIONS}
            onSortChange={(sortBy) => onControlsChange({ sortBy })}
            shownCount={templateView.shownCount}
            totalCount={templateView.totalCount}
            resultLabel="templates"
            chips={activeChips}
            onClear={() => onControlsChange(createTemplateControls())}
          />

          {templateView.shownCount === 0 ? (
            <EmptyFilterState
              title="No templates match the current filters"
              description="Try a broader search or clear the filters to browse every template again."
              onClear={() => onControlsChange(createTemplateControls())}
            />
          ) : (
            <div className="df-group-stack">
              {templateView.groups.map((group) => (
                <section key={group.key} className="df-group-section">
                  <div className="df-group-header">
                    <span>{group.label}</span>
                    <span>{group.items.length}</span>
                  </div>
                  <div className="df-list-stack">
                    {group.items.map((entry) => {
                      const linkedFormCount = formCounts[entry.id] || 0;

                      return (
                        <div key={entry.id} className="list-item">
                          <div className="list-item-info">
                            <div className="list-item-title">{entry.name}</div>
                            <div className="list-item-subtitle">
                              {entry.category} | {entry.sections?.length || 0} sections | {linkedFormCount === 1 ? '1 linked form' : `${linkedFormCount} linked forms`} | {entry.description?.slice(0, 80) || 'No description'}
                            </div>
                          </div>
                          <div className="df-action-group">
                            <button
                              className="btn btn-primary btn-sm"
                              onClick={() => (onSelect ? onSelect(entry) : handleStartEdit(entry))}
                            >
                              Edit
                            </button>
                            <button className="btn btn-danger btn-sm" onClick={() => onDelete(entry.id)}>
                              Delete
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </section>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
const TemplateEditor = ({ template, onSave, onCancel }) => {
  const [currentTemplate, setCurrentTemplate] = useState(cloneTemplate(template));
  const [activeSection, setActiveSection] = useState(null);
  const [showFieldModal, setShowFieldModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [fieldFormData, setFieldFormData] = useState(createDefaultFieldFormData());
  const [draggingFieldId, setDraggingFieldId] = useState(null);
  const [draggingFieldSectionId, setDraggingFieldSectionId] = useState(null);
  const [dragOverFieldId, setDragOverFieldId] = useState(null);
  const [dragOverFieldSectionId, setDragOverFieldSectionId] = useState(null);
  const [dragOverFieldPosition, setDragOverFieldPosition] = useState(null);
  const [dragOverSectionFieldZone, setDragOverSectionFieldZone] = useState(null);
  const [draggingSectionId, setDraggingSectionId] = useState(null);
  const [dragOverSectionTarget, setDragOverSectionTarget] = useState(null);
  const [previewValues, setPreviewValues] = useState({});

  useEffect(() => {
    setCurrentTemplate(cloneTemplate(template));
    setPreviewValues({});
  }, [template]);

  const addSection = () => {
    const newSection = createSection('New Section');
    setCurrentTemplate({
      ...currentTemplate,
      sections: [...currentTemplate.sections, newSection],
    });
    setActiveSection(newSection.id);
  };

  const updateSection = (sectionId, updates) => {
    setCurrentTemplate({
      ...currentTemplate,
      sections: currentTemplate.sections.map((section) =>
        section.id === sectionId ? { ...section, ...updates } : section
      ),
    });
  };

  const deleteSection = (sectionId) => {
    if (window.confirm('Are you sure you want to delete this section?')) {
      setCurrentTemplate({
        ...currentTemplate,
        sections: currentTemplate.sections.filter((section) => section.id !== sectionId),
      });
      if (activeSection === sectionId) {
        setActiveSection(null);
      }
    }
  };

  const addField = (sectionId) => {
    setActiveSection(sectionId);
    setEditingField(null);
    setFieldFormData(createDefaultFieldFormData());
    setShowFieldModal(true);
  };

  const editField = (sectionId, field) => {
    setActiveSection(sectionId);
    setEditingField(field);
    setFieldFormData({
      type: field.type,
      label: field.label,
      placeholder: field.placeholder || '',
      required: field.required || false,
      options: field.options?.map((option) => `${option.value}:${option.label}`).join('\n') || '',
      layout: {
        order: field.layout?.order ?? '',
        span: field.layout?.span ?? '',
        align: field.layout?.align || 'left',
      },
    });
    setShowFieldModal(true);
  };

  const clearFieldDragState = () => {
    setDraggingFieldId(null);
    setDraggingFieldSectionId(null);
    setDragOverFieldId(null);
    setDragOverFieldSectionId(null);
    setDragOverFieldPosition(null);
    setDragOverSectionFieldZone(null);
  };

  const clearSectionDragState = () => {
    setDraggingSectionId(null);
    setDragOverSectionTarget(null);
  };

  const reorderSections = (sourceSectionId, targetSectionId, insertBefore = true) => {
    if (!sourceSectionId || !targetSectionId || sourceSectionId === targetSectionId) {
      return;
    }

    setCurrentTemplate((previousTemplate) => ({
      ...previousTemplate,
      sections: (() => {
        const sections = [...previousTemplate.sections];
        const sourceIndex = sections.findIndex((section) => section.id === sourceSectionId);
        const targetIndex = sections.findIndex((section) => section.id === targetSectionId);

        if (sourceIndex === -1 || targetIndex === -1) {
          return previousTemplate.sections;
        }

        const [movedSection] = sections.splice(sourceIndex, 1);
        const adjustedTargetIndex = sourceIndex < targetIndex ? targetIndex - 1 : targetIndex;
        const insertIndex = insertBefore ? adjustedTargetIndex : adjustedTargetIndex + 1;

        sections.splice(insertIndex, 0, movedSection);

        return sections;
      })(),
    }));
  };

  const moveFieldAcrossSections = (
    sourceSectionId,
    sourceFieldId,
    targetSectionId,
    targetFieldId = null,
    insertPosition = 'after'
  ) => {
    if (!sourceSectionId || !sourceFieldId || !targetSectionId) {
      return;
    }

    setCurrentTemplate((previousTemplate) => {
      const sourceSection = previousTemplate.sections.find((section) => section.id === sourceSectionId);
      const targetSection = previousTemplate.sections.find((section) => section.id === targetSectionId);

      if (!sourceSection || !targetSection) {
        return previousTemplate;
      }

      const orderedSourceFields = sortFieldsByLayoutOrder(sourceSection.fields);
      const sourceField = orderedSourceFields.find((field) => field.id === sourceFieldId);
      if (!sourceField) {
        return previousTemplate;
      }

      const normalizedSourceFieldsWithoutDragged = normalizeSectionFieldOrder(
        orderedSourceFields.filter((field) => field.id !== sourceFieldId)
      );

      const orderedTargetFields =
        sourceSectionId === targetSectionId
          ? normalizedSourceFieldsWithoutDragged
          : sortFieldsByLayoutOrder(targetSection.fields);

      const reorderedTargetFields = [...orderedTargetFields];
      let targetInsertIndex = reorderedTargetFields.length;

      if (targetFieldId) {
        const targetIndex = orderedTargetFields.findIndex((field) => field.id === targetFieldId);
        if (targetIndex !== -1) {
          targetInsertIndex = insertPosition === 'after' ? targetIndex + 1 : targetIndex;
        }
      }

      reorderedTargetFields.splice(targetInsertIndex, 0, sourceField);

      return {
        ...previousTemplate,
        sections: previousTemplate.sections.map((section) => {
          if (section.id === sourceSectionId && section.id === targetSectionId) {
            return {
              ...section,
              fields: normalizeSectionFieldOrder(reorderedTargetFields),
            };
          }

          if (section.id === sourceSectionId) {
            return {
              ...section,
              fields: normalizedSourceFieldsWithoutDragged,
            };
          }

          if (section.id === targetSectionId) {
            return {
              ...section,
              fields: normalizeSectionFieldOrder(reorderedTargetFields),
            };
          }

          return section;
        }),
      };
    });
  };

  const handleSectionDragStart = (event, sectionId) => {
    event.dataTransfer.setData('text/plain', sectionId);
    event.dataTransfer.effectAllowed = 'move';
    clearFieldDragState();
    setDraggingSectionId(sectionId);
  };

  const handleSectionDragOver = (event, sectionId, zone) => {
    if (!draggingSectionId) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
    setDragOverSectionTarget({ sectionId, zone });
  };

  const handleSectionDragLeave = (sectionId, zone) => {
    if (dragOverSectionTarget?.sectionId === sectionId && dragOverSectionTarget?.zone === zone) {
      setDragOverSectionTarget(null);
    }
  };

  const handleSectionDrop = (event, sectionId, zone) => {
    event.preventDefault();

    if (!draggingSectionId || draggingSectionId === sectionId) {
      clearSectionDragState();
      return;
    }

    reorderSections(draggingSectionId, sectionId, zone === 'top');
    clearSectionDragState();
  };

  const handleFieldDragStart = (event, sectionId, fieldId) => {
    event.dataTransfer.setData('text/plain', fieldId);
    event.dataTransfer.effectAllowed = 'move';
    clearSectionDragState();
    setDraggingFieldId(fieldId);
    setDraggingFieldSectionId(sectionId);
    setDragOverFieldId(fieldId);
    setDragOverFieldSectionId(sectionId);
    setDragOverFieldPosition('before');
  };

  const getFieldDropPositionFromEvent = (event) => {
    const box = event.currentTarget.getBoundingClientRect();
    return event.clientY - box.top < box.height / 2 ? 'before' : 'after';
  };

  const handleFieldDragOver = (event, sectionId, fieldId) => {
    if (!draggingFieldId) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    const nextPosition = getFieldDropPositionFromEvent(event);

    if (
      dragOverFieldId !== fieldId ||
      dragOverFieldSectionId !== sectionId ||
      dragOverFieldPosition !== nextPosition
    ) {
      setDragOverFieldId(fieldId);
      setDragOverFieldSectionId(sectionId);
      setDragOverFieldPosition(nextPosition);
      setDragOverSectionFieldZone(null);
    }
  };

  const handleFieldDragLeave = (fieldId, sectionId) => {
    if (dragOverFieldId === fieldId && dragOverFieldSectionId === sectionId) {
      setDragOverFieldId(null);
      setDragOverFieldSectionId(null);
      setDragOverFieldPosition(null);
    }
  };

  const handleFieldDrop = (event, sectionId, fieldId, targetPosition = null) => {
    event.preventDefault();
    if (!draggingFieldId || !draggingFieldSectionId) {
      clearFieldDragState();
      return;
    }

    if (draggingFieldId === fieldId && draggingFieldSectionId === sectionId) {
      clearFieldDragState();
      return;
    }

    const effectivePosition = targetPosition || (dragOverFieldSectionId === sectionId && dragOverFieldId === fieldId
      ? dragOverFieldPosition
      : 'before');

    moveFieldAcrossSections(draggingFieldSectionId, draggingFieldId, sectionId, fieldId, effectivePosition);
    clearFieldDragState();
  };

  const handleSectionFieldDropZoneOver = (event, sectionId, zone) => {
    if (!draggingFieldId) {
      return;
    }

    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';

    setDragOverFieldId(null);
    setDragOverFieldSectionId(sectionId);
    setDragOverFieldPosition(zone === 'top' ? 'before' : 'after');
    setDragOverSectionFieldZone({ sectionId, zone });
  };

  const handleSectionFieldDropZoneLeave = (sectionId, zone) => {
    if (dragOverSectionFieldZone?.sectionId === sectionId && dragOverSectionFieldZone?.zone === zone) {
      setDragOverSectionFieldZone(null);
      if (dragOverFieldSectionId === sectionId) {
        setDragOverFieldId(null);
        setDragOverFieldSectionId(null);
        setDragOverFieldPosition(null);
      }
    }
  };

  const handleSectionFieldDropZoneDrop = (event, sectionId, zone) => {
    event.preventDefault();
    if (!draggingFieldId || !draggingFieldSectionId) {
      clearFieldDragState();
      return;
    }

    const nextPosition = zone === 'top' ? 'before' : 'after';
    moveFieldAcrossSections(draggingFieldSectionId, draggingFieldId, sectionId, null, nextPosition);
    clearFieldDragState();
  };

  const saveField = () => {
    const isLabelField = fieldFormData.type === FIELD_TYPES.LABEL;
    const fieldName = isLabelField ? undefined : (editingField ? editingField.name : createId());

    const nextLayout = {};
    const parsedSpan = toIntOrNull(fieldFormData.layout?.span);
    const parsedOrder = toIntOrNull(fieldFormData.layout?.order);
    const alignValue = String(fieldFormData.layout?.align || 'left').toLowerCase();
    const normalizedAlign = alignValue === 'center' || alignValue === 'right' ? alignValue : 'left';

    nextLayout.align = normalizedAlign;
    if (parsedSpan !== null && parsedSpan >= 1 && parsedSpan <= 12) {
      nextLayout.span = parsedSpan;
    }

    if (parsedOrder !== null && parsedOrder > 0) {
      nextLayout.order = parsedOrder;
    } else if (editingField?.layout?.order) {
      nextLayout.order = editingField.layout.order;
    } else if (!editingField) {
      const sectionFieldCount =
        currentTemplate.sections.find((section) => section.id === activeSection)?.fields?.length || 0;
      nextLayout.order = sectionFieldCount + 1;
    }

    const newField = createField(fieldFormData.type, {
      label: fieldFormData.label,
      name: fieldName,
      placeholder: isLabelField ? undefined : fieldFormData.placeholder,
      required: isLabelField ? false : fieldFormData.required,
      layout: Object.keys(nextLayout).length > 0 ? nextLayout : undefined,
    });

    if (
      (fieldFormData.type === FIELD_TYPES.SELECT || fieldFormData.type === FIELD_TYPES.CHECKBOX_GROUP) &&
      fieldFormData.options
    ) {
      newField.options = fieldFormData.options.split('\n').map((line) => {
        const [value, ...labelParts] = line.split(':');
        const optionValue = value.trim();
        return {
          value: optionValue,
          label: labelParts.join(':').trim() || optionValue,
        };
      });
    }

    const updatedSections = currentTemplate.sections.map((section) => {
      if (section.id !== activeSection) {
        return section;
      }

      if (editingField) {
        return {
          ...section,
          fields: normalizeSectionFieldOrder(
            section.fields.map((field) => (field.id === editingField.id ? { ...newField, id: field.id } : field))
          ),
        };
      }

      return {
        ...section,
        fields: normalizeSectionFieldOrder([...section.fields, newField]),
      };
    });

    setCurrentTemplate({ ...currentTemplate, sections: updatedSections });
    setShowFieldModal(false);
    setEditingField(null);
    setFieldFormData(createDefaultFieldFormData());
  };

  const deleteField = (sectionId, fieldId) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      setCurrentTemplate({
        ...currentTemplate,
        sections: currentTemplate.sections.map((section) =>
          section.id === sectionId
            ? {
                ...section,
                fields: normalizeSectionFieldOrder(section.fields.filter((field) => field.id !== fieldId)),
              }
            : section
        ),
      });
    }
  };

  const moveSection = (sectionId, direction) => {
    const index = currentTemplate.sections.findIndex((section) => section.id === sectionId);
    if (index === -1) {
      return;
    }

    if (direction === 'up' && index > 0) {
      reorderSections(sectionId, currentTemplate.sections[index - 1].id, true);
      return;
    }

    if (direction === 'down' && index < currentTemplate.sections.length - 1) {
      reorderSections(sectionId, currentTemplate.sections[index + 1].id, false);
    }
  };


  const setFieldLayoutPreset = (presetKey) => {
    setFieldFormData({
      ...fieldFormData,
      layout: {
        ...fieldFormData.layout,
        span: getSpanFromPreset(presetKey),
      },
    });
  };

  const setFieldAlign = (alignValue) => {
    setFieldFormData({
      ...fieldFormData,
      layout: {
        ...fieldFormData.layout,
        align: alignValue,
      },
    });
  };
  const handlePreviewFieldChange = (fieldName, value) => {
    if (!fieldName) {
      return;
    }

    setPreviewValues((previousValues) => ({
      ...previousValues,
      [fieldName]: value,
    }));
  };

  return (
    <div>
      <div className="template-editor-layout">
        <div className="template-editor-main">
          <div className="card">
            <div className="df-toolbar">
              <div className="df-toolbar-title">
                <h2 className="card-title">{currentTemplate.name}</h2>
                <p style={{ color: '#666', marginTop: '5px' }}>{currentTemplate.description}</p>
              </div>
              <div className="df-action-group">
                <button className="btn btn-primary" onClick={() => onSave(currentTemplate)}>
                  Save Template
                </button>
                <button className="btn btn-secondary" onClick={onCancel}>
                  Cancel
                </button>
              </div>
            </div>

            <div className="df-action-group" style={{ marginBottom: '20px' }}>
              <button className="btn btn-primary" onClick={addSection}>
                + Add Section
              </button>
            </div>

            {currentTemplate.sections.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666', background: '#f8f9fa', borderRadius: '8px' }}>
                <p>No sections yet. Add your first section to start building the form.</p>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              {currentTemplate.sections.map((section, index) => {
                const orderedFields = sortFieldsByLayoutOrder(section.fields);
                const isSectionDragging = draggingSectionId === section.id;

                return (
                  <div
                    key={section.id}
                    className={`section-builder ${isSectionDragging ? 'is-dragging' : ''}`}
                  >
                    <div
                      className={`section-reorder-zone section-reorder-zone--top ${
                        dragOverSectionTarget?.sectionId === section.id && dragOverSectionTarget?.zone === 'top'
                          ? 'is-active'
                          : ''
                      }`}
                      onDragOver={(event) => handleSectionDragOver(event, section.id, 'top')}
                      onDragLeave={() => handleSectionDragLeave(section.id, 'top')}
                      onDrop={(event) => handleSectionDrop(event, section.id, 'top')}
                    />
                    <div className="section-builder-header">
                      <button
                        className="section-builder-drag-handle"
                        type="button"
                        draggable
                        onDragStart={(event) => handleSectionDragStart(event, section.id)}
                        onDragEnd={clearSectionDragState}
                        title="Reorder section"
                      >
                        Move
                      </button>
                      <input
                        type="text"
                        className="section-title-input"
                        value={section.title}
                        onChange={(event) => updateSection(section.id, { title: event.target.value })}
                        placeholder="Section Title"
                      />
                      <div className="df-action-group">
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => moveSection(section.id, 'up')}
                          disabled={index === 0}
                        >
                          Up
                        </button>
                        <button
                          className="btn btn-sm btn-secondary"
                          onClick={() => moveSection(section.id, 'down')}
                          disabled={index === currentTemplate.sections.length - 1}
                        >
                          Down
                        </button>
                        <button className="btn btn-sm btn-primary" onClick={() => addField(section.id)}>
                          + Field
                        </button>
                        <button className="btn btn-sm btn-danger" onClick={() => deleteSection(section.id)}>
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="template-field-list">
                      <div
                        className={`section-field-drop-zone section-field-drop-zone--top ${
                          dragOverSectionFieldZone?.sectionId === section.id && dragOverSectionFieldZone?.zone === 'top'
                            ? 'is-active'
                            : ''
                        }`}
                        onDragOver={(event) => handleSectionFieldDropZoneOver(event, section.id, 'top')}
                        onDragLeave={() => handleSectionFieldDropZoneLeave(section.id, 'top')}
                        onDrop={(event) => handleSectionFieldDropZoneDrop(event, section.id, 'top')}
                      />

                      {orderedFields.length === 0 ? (
                        <p className="template-field-empty" style={{ color: '#666', fontStyle: 'italic' }}>
                          No fields in this section yet.
                        </p>
                      ) : (
                        orderedFields.map((field) => {
                          const fieldAlign = getFieldLayoutAlign(field);
                          const alignLabel = fieldAlign.charAt(0).toUpperCase() + fieldAlign.slice(1);
                          const widthLabel = field.layout?.span ? `${field.layout.span}/12` : 'auto';
                          const dragClass = draggingFieldId === field.id ? 'is-dragging' : '';
                          const isOverField = dragOverFieldId === field.id && dragOverFieldSectionId === section.id;
                          const overClass = isOverField ? 'is-drag-over' : '';
                          const overBeforeClass = isOverField && dragOverFieldPosition === 'before'
                            ? 'is-drag-over-before'
                            : '';
                          const overAfterClass = isOverField && dragOverFieldPosition === 'after'
                            ? 'is-drag-over-after'
                            : '';

                          return (
                            <div
                              key={field.id}
                              className={`field-builder field-builder-sortable ${dragClass} ${overClass} ${overBeforeClass} ${overAfterClass}`}
                              draggable
                              onDragStart={(event) => handleFieldDragStart(event, section.id, field.id)}
                              onDragOver={(event) => handleFieldDragOver(event, section.id, field.id)}
                              onDrop={(event) => handleFieldDrop(event, section.id, field.id)}
                              onDragEnd={clearFieldDragState}
                              onDragLeave={() => handleFieldDragLeave(field.id, section.id)}
                            >
                              <div className="field-builder-header">
                                <span style={{ fontWeight: 500 }}>
                                  {field.label}
                                  <span style={{ color: '#666', marginLeft: '8px', fontSize: '0.85rem' }}>
                                    ({field.type})
                                  </span>
                                  {field.required && <span style={{ color: 'var(--error)', marginLeft: '4px' }}>*</span>}
                                  <span className="field-builder-drag-handle">drag</span>
                                </span>
                                <div className="df-action-group">
                                  <button className="btn btn-sm btn-secondary" onClick={() => editField(section.id, field)}>
                                    Edit
                                  </button>
                                  <button className="btn btn-sm btn-danger" onClick={() => deleteField(section.id, field.id)}>
                                    Delete
                                  </button>
                                </div>
                              </div>
                              <div style={{ marginTop: '4px', color: '#666', fontSize: '0.85rem' }}>
                          Field key: {field.name} | Order: {field.layout?.order || 'auto'} | Width: {widthLabel} | Align: {alignLabel}
                              </div>
                            </div>
                          );
                        })
                      )}

                      <div
                        className={`section-field-drop-zone section-field-drop-zone--bottom ${
                          dragOverSectionFieldZone?.sectionId === section.id && dragOverSectionFieldZone?.zone === 'bottom'
                            ? 'is-active'
                            : ''
                        }`}
                        onDragOver={(event) => handleSectionFieldDropZoneOver(event, section.id, 'bottom')}
                        onDragLeave={() => handleSectionFieldDropZoneLeave(section.id, 'bottom')}
                        onDrop={(event) => handleSectionFieldDropZoneDrop(event, section.id, 'bottom')}
                      />
                    </div>

                    <div
                      className={`section-reorder-zone section-reorder-zone--bottom ${
                        dragOverSectionTarget?.sectionId === section.id && dragOverSectionTarget?.zone === 'bottom'
                          ? 'is-active'
                          : ''
                      }`}
                      onDragOver={(event) => handleSectionDragOver(event, section.id, 'bottom')}
                      onDragLeave={() => handleSectionDragLeave(section.id, 'bottom')}
                      onDrop={(event) => handleSectionDrop(event, section.id, 'bottom')}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <TemplateBuilderLivePreview
          template={currentTemplate}
          values={previewValues}
          onFieldChange={handlePreviewFieldChange}
        />
      </div>

      {showFieldModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 className="modal-title">{editingField ? 'Edit Field' : 'Add Field'}</h3>
              <button className="btn btn-sm btn-secondary" onClick={() => setShowFieldModal(false)}>x</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Field Type</label>
                <select
                  className="form-select"
                  value={fieldFormData.type}
                  onChange={(event) => setFieldFormData({ ...fieldFormData, type: event.target.value })}
                >
                  <option value={FIELD_TYPES.TEXT}>Text Input</option>
                  <option value={FIELD_TYPES.TEXTAREA}>Text Area</option>
                  <option value={FIELD_TYPES.NUMBER}>Number</option>
                  <option value={FIELD_TYPES.EMAIL}>Email</option>
                  <option value={FIELD_TYPES.DATE}>Date</option>
                  <option value={FIELD_TYPES.TIME}>Time</option>
                  <option value={FIELD_TYPES.SELECT}>Dropdown</option>
                  <option value={FIELD_TYPES.CHECKBOX}>Checkbox</option>
                  <option value={FIELD_TYPES.CHECKBOX_GROUP}>Checkbox Group</option>
                  <option value={FIELD_TYPES.TABLE}>Table</option>
                  <option value={FIELD_TYPES.LABEL}>Label</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">Label *</label>
                <input
                  type="text"
                  className="form-input"
                  value={fieldFormData.label}
                  onChange={(event) => setFieldFormData({ ...fieldFormData, label: event.target.value })}
                  required
                />
              </div>

              {fieldFormData.type !== FIELD_TYPES.LABEL && (
                <div className="form-group">
                  <div className="field-note" style={{ marginTop: '8px' }}>
                    Field key will be auto-generated
                  </div>
                </div>
              )}

              {(fieldFormData.type === FIELD_TYPES.TEXT || fieldFormData.type === FIELD_TYPES.TEXTAREA) && (
                <div className="form-group">
                  <label className="form-label">Placeholder</label>
                  <input
                    type="text"
                    className="form-input"
                    value={fieldFormData.placeholder}
                    onChange={(event) => setFieldFormData({ ...fieldFormData, placeholder: event.target.value })}
                  />
                </div>
              )}

              {(fieldFormData.type === FIELD_TYPES.SELECT || fieldFormData.type === FIELD_TYPES.CHECKBOX_GROUP) && (
                <div className="form-group">
                  <label className="form-label">Options (one per line, format: value:label)</label>
                  <textarea
                    className="form-textarea"
                    value={fieldFormData.options}
                    onChange={(event) => setFieldFormData({ ...fieldFormData, options: event.target.value })}
                    placeholder="option1:Option 1\noption2:Option 2\noption3:Option 3"
                    rows={4}
                  />
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Display order (optional)</label>
                <input
                  type="number"
                  className="form-input"
                  min="1"
                  value={fieldFormData.layout?.order || ''}
                  onChange={(event) =>
                    setFieldFormData({
                      ...fieldFormData,
                      layout: { ...fieldFormData.layout, order: event.target.value },
                    })
                  }
                  placeholder="e.g. 1"
                />
                <div className="field-note" style={{ marginTop: '8px' }}>
                  Lower numbers are shown first inside the section.
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Width preset</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(90px, 1fr))', gap: '8px' }}>
                  {FIELD_WIDTH_PRESETS.map((preset) => {
                    const isActive = getPresetForSpan(fieldFormData.layout?.span) === preset.key;
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        className={`btn ${isActive ? 'btn-primary' : 'btn-outline'} btn-sm`}
                        onClick={() => setFieldLayoutPreset(preset.key)}
                        title={`Set width to ${preset.label}`}
                      >
                        {preset.label}
                        {preset.span ? ` (${preset.span}/12)` : ''}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">Alignment</label>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: '8px' }}>
                  {FIELD_ALIGNMENT_OPTIONS.map((option) => {
                    const isActive = (fieldFormData.layout?.align || 'left') === option.key;
                    return (
                      <button
                        key={option.key}
                        type="button"
                        className={`btn ${isActive ? 'btn-primary' : 'btn-outline'} btn-sm`}
                        onClick={() => setFieldAlign(option.key)}
                        title={`Set alignment to ${option.label.toLowerCase()}`}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>
              </div>


              {fieldFormData.type !== FIELD_TYPES.LABEL && (
                <div className="form-group">
                  <div className="checkbox-item">
                    <input
                      type="checkbox"
                      id="required"
                      checked={fieldFormData.required}
                      onChange={(event) => setFieldFormData({ ...fieldFormData, required: event.target.checked })}
                    />
                    <label htmlFor="required">Required field</label>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowFieldModal(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={saveField} disabled={!fieldFormData.label}>
                Save Field
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const TemplateBuilderLivePreview = ({ template, values = {}, onFieldChange }) => {
  const getDefaultValue = (field) => {
    if (!isAnswerableField(field)) {
      return undefined;
    }

    if (field.type === FIELD_TYPES.CHECKBOX_GROUP || field.type === FIELD_TYPES.TABLE) {
      return [];
    }

    return '';
  };

  const handleFieldValueChange = (fieldName, value) => {
    if (!fieldName || !onFieldChange) {
      return;
    }

    onFieldChange(fieldName, value);
  };

  return (
    <div className="template-editor-preview">
      <div className="card template-live-preview-card">
        <div className="df-toolbar">
          <div className="df-toolbar-title">
            <h2 className="card-title">Live Form Preview</h2>
            <p className="template-live-preview-subtitle">
              This panel updates field order, width, and alignment instantly.
            </p>
          </div>
        </div>

        {!template.sections || template.sections.length === 0 ? (
          <div className="df-empty-state">Add sections and fields to see a live preview.</div>
        ) : (
          <div className="df-form-sections">
            {template.sections.map((section) => {
              const orderedFields = sortFieldsByLayoutOrder(section.fields || []);

              return (
                <section key={section.id} className="df-form-section">
                  <div className="df-form-section-header">
                    <h3 className="df-form-section-title">
                      {section.icon} {section.title}
                    </h3>
                    <p className="df-form-section-subtitle">
                      {orderedFields.length} {orderedFields.length === 1 ? 'field' : 'fields'}
                    </p>
                  </div>

                  {orderedFields.length === 0 ? (
                    <div className="df-empty-state">No fields in this section.</div>
                  ) : (
                    <div className="df-form-grid">
                      {orderedFields.map((field) => {
                        const baseFieldName = field.name || `field_${field.id}`;
                        const sourceFieldName = getFieldMetadataName(baseFieldName, 'source');
                        const noteFieldName = getFieldMetadataName(baseFieldName, 'note');
                        const fieldSpan = getDisplayFieldSpan(field);
                        const fieldAlign = getFieldLayoutAlign(field);
                        const fieldValue = isAnswerableField(field) ? values[baseFieldName] ?? getDefaultValue(field) : undefined;

                        return (
                          <article
                            key={field.id}
                            className={`df-form-field df-form-field--span-${fieldSpan} df-form-field--align-${fieldAlign}`}
                          >
                            <FormField
                              field={field}
                              value={fieldValue}
                              onChange={(name, value) => handleFieldValueChange(baseFieldName, value)}
                              readOnly={false}
                            />
                            <details className="df-metadata-section df-field-metadata">
                              <summary>Source and note</summary>
                              <div className="field-metadata">
                                <div className="form-group" style={{ marginTop: '12px', marginBottom: '12px' }}>
                                  <label className="form-label">Source (interview reference)</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    value={values[sourceFieldName] || ''}
                                    onChange={(event) =>
                                      handleFieldValueChange(sourceFieldName, event.target.value)
                                    }
                                  />
                                </div>
                                <div className="form-group" style={{ marginTop: '12px' }}>
                                  <label className="form-label">Note</label>
                                  <textarea
                                    className="form-textarea"
                                    rows={3}
                                    value={values[noteFieldName] || ''}
                                    onChange={(event) =>
                                      handleFieldValueChange(noteFieldName, event.target.value)
                                    }
                                  />
                                </div>
                              </div>
                            </details>
                          </article>
                        );
                      })}
                    </div>
                  )}
                </section>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TemplateBuilder;



